import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TriangleAlert } from 'lucide-react';
import { usePermission } from '@/Hooks/usePermission';
import type { PlanLimitRefusal } from '../planLimits';

/**
 * THE DOOR IN THE WALL.
 *
 * A tenant on STARTER pressing "Add branch" is genuinely refused by
 * `SubscriptionsService.assertWithinPlanLimits`, and before this component the entire answer
 * was a red toast carrying the server's English prose — untranslatable, gone in four seconds,
 * and naming no way forward. An operator reading it had no reason to believe the wall had a
 * door at all.
 *
 * So this is a panel and not a toast: it stays on screen inside the dialog that was refused,
 * it says WHICH ceiling was hit and by how much in the reader's own language, and it ends in
 * the one link that resolves it.
 *
 * The link is shown only to someone who could actually open the plan screen. Offering a door
 * that answers 403 is the same failure one level down — and a person without `settings.view`
 * still needs to know why their create failed, so the sentence stays and only the link goes.
 */
export default function PlanLimitNotice({ refusal }: { refusal: PlanLimitRefusal }) {
  const { t } = useTranslation();
  const { can } = usePermission();

  const plan = refusal.planName ?? t('settings.planLimit.currentPlan');

  return (
    <div
      role="alert"
      className="flex gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm dark:border-amber-800 dark:bg-amber-950/40"
    >
      <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
      <div className="min-w-0 space-y-1">
        <p className="font-medium">{t('settings.planLimit.title')}</p>
        <p className="text-ink-muted">
          {t(`settings.planLimit.body.${refusal.resource}`, {
            plan,
            limit: refusal.limit,
            used: refusal.used,
          })}
        </p>
        {can('settings.view') && (
          <Link to="/settings/plan" className="inline-block font-medium text-accent underline">
            {t('settings.planLimit.action')}
          </Link>
        )}
      </div>
    </div>
  );
}
